import { ICompany } from './../../companies/api/company.interface';
import { IStore } from './../../stores/api/store.interface';
import { IRefreshToken, IAllowedUser } from './../api/admin.interfaces';
import { adminService } from '../api/admin.api';
import { Module, GetterTree, MutationTree, ActionTree, Commit, ActionContext } from 'vuex';
import { IVuexState } from '@/store/store';
import { IUser } from './../../account/api/user.interfaces';
import { findWithAttr } from '../../../store/store';

export interface IAdminState {
  users?: IUser[];
  companies?: ICompany[];
  stores?: IStore[];
  refreshTokens?: IRefreshToken[];
  allowedUsers?: IAllowedUser[];
}

const state: IAdminState = {
  users: undefined,
  companies: undefined,
  stores: undefined,
  refreshTokens: undefined,
  allowedUsers: undefined,
};

const getters: GetterTree<IAdminState, IVuexState> = {
  users: (state: IAdminState) => state.users,
  companies: (state: IAdminState) => state.companies,
  stores: (state: IAdminState) => state.stores,
  refreshTokens: (state: IAdminState) => state.refreshTokens,
  allowedUsers: (state: IAdminState) => state.allowedUsers,
};

const mutations: MutationTree<IAdminState> = {
  reset: (state: IAdminState) => {
    state.users = undefined;
    state.companies = undefined;
    state.stores = undefined;
    state.refreshTokens = undefined;
    state.allowedUsers = undefined;
  },

  setUsers: (state: IAdminState, users: IUser[]) => {
    state.users = users;
  },

  deleteUser: (state: IAdminState, id: string) => {
    if (state.users) {
      const index = findWithAttr(state.users, 'id', id);
      state.users.splice(index, 1);
    }
  },

  setCompanies: (state: IAdminState, companies: ICompany[]) => {
    state.companies = companies;
  },

  deleteCompany: (state: IAdminState, id: string) => {
    if (state.companies) {
      const index = findWithAttr(state.companies, 'id', id);
      state.companies.splice(index, 1);
    }
  },

  setStores: (state: IAdminState, stores: IStore[]) => {
    state.stores = stores;
  },

  deleteStore: (state: IAdminState, id: string) => {
    if (state.stores) {
      const index = findWithAttr(state.stores, 'id', id);
      state.stores.splice(index, 1);
    }
  },

  setRefreshTokens: (state: IAdminState, refreshTokens: IRefreshToken[]) => {
    state.refreshTokens = refreshTokens;
  },

  deleteRefreshToken: (state: IAdminState, id: string) => {
    if (state.refreshTokens) {
      const index = findWithAttr(state.refreshTokens, 'id', id);
      state.refreshTokens.splice(index, 1);
    }
  },

  setAllowedUsers: (state: IAdminState, allowedUsers: IAllowedUser[]) => {
    state.allowedUsers = allowedUsers;
  },

  deleteAllowedUser: (state: IAdminState, id: string) => {
    if (state.allowedUsers) {
      const index = findWithAttr(state.allowedUsers, 'id', id);
      state.allowedUsers.splice(index, 1);
    }
  },

  addAllowedUser: (state: IAdminState, allowedUser: IAllowedUser) => {
    if (!state.allowedUsers) state.allowedUsers = [];
    state.allowedUsers.push(allowedUser);
  },
};

const actions: ActionTree<IAdminState, IVuexState> = {
  reset: ({ commit }: { commit: Commit }) => {
    commit('reset');
  },

  getUsers({ commit }: { commit: Commit }) {
    return adminService.getUsers().then((response: IApi<IUser[]>) => {
      commit('setUsers', response.result);
    });
  },

  deleteUser({ commit }: { commit: Commit }, id: string) {
    return adminService.deleteUser(id).then((response: IApi<string>) => {
      commit('deleteUser', id);
    });
  },

  getCompanies({ commit }: { commit: Commit }) {
    return adminService.getCompanies().then((response: IApi<ICompany[]>) => {
      commit('setCompanies', response.result);
    });
  },

  deleteCompany({ commit }: { commit: Commit }, id: string) {
    return adminService.deleteCompany(id).then((response: IApi<string>) => {
      commit('deleteCompany', id);
    });
  },

  getStores({ commit }: { commit: Commit }) {
    return adminService.getStores().then((response: IApi<IStore[]>) => {
      commit('setStores', response.result);
    });
  },

  deleteStore({ commit }: { commit: Commit }, id: string) {
    return adminService.deleteStore(id).then((response: IApi<string>) => {
      commit('deleteStore', id);
    });
  },

  getRefreshTokens({ commit }: { commit: Commit }) {
    return adminService.getRefreshTokens().then((response: IApi<IRefreshToken[]>) => {
      commit('setRefreshTokens', response.result);
    });
  },

  deleteRefreshToken({ commit }: { commit: Commit }, id: string) {
    return adminService.deleteRefreshToken(id).then((response: IApi<string>) => {
      commit('deleteRefreshToken', id);
    });
  },

  getAllowedUsers({ commit }: { commit: Commit }) {
    return adminService.getAllowedUsers().then((response: IApi<IAllowedUser[]>) => {
      commit('setAllowedUsers', response.result);
    });
  },

  deleteAllowedUser({ commit }: { commit: Commit }, id: string) {
    return adminService.deleteAllowedUser(id).then((response: IApi<string>) => {
      commit('deleteAllowedUser', id);
    });
  },

  addAllowedUser({ commit }: { commit: Commit }, allowedUser: IAllowedUser) {
    return adminService.addAllowedUser(allowedUser).then((response: IApi<IAllowedUser>) => {
      commit('addAllowedUser', response.result);
    });
  },
};

const namespaced: boolean = true;

export const admin: Module<IAdminState, IVuexState> = { namespaced, state, getters, actions, mutations };
